import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import qs from 'qs';
import noop from 'lodash/noop';

import { getInstance as getBootstrap } from 'utils/bootstrap';
import { isValidEmail } from 'utils/textFormats';
import { SuiContainer, SuiModalContent, SuiTextInput, SuiButton } from '@splashthat/sui';
import Recaptcha from 'common/components/Recaptcha';

import { HeaderText, RecaptchaContainer, SubmitButtonContainer, Asterisk, InputContainer } from './Form.styles';
import {
    formKeys,
    STATUS_IDLE,
    STATUS_SUCCESS,
    STATUS_LOADING,
    STATUS_ERROR,
    initState,
    reducer,
    setValue,
    setError,
    setSubmissionStatus,
    getFormValue,
    getFormError,
    getSubmissionStatus,
} from './Form.state';

const bootstrap = getBootstrap();

const HEADER_TITLE = 'Contact the Organizer';
const LABEL_NAME = 'Name';
const PLACEHOLDER_NAME = 'Enter your name';
const LABEL_EMAIL = 'Email';
const PLACEHOLDER_EMAIL = 'you@you.email';
const LABEL_COMPANY = 'Company';
const LABEL_NOTE = 'Message';
const LABEL_SUBMISSION_ERROR = 'There has been an error submitting the note.';
const LABEL_SEND_MESSAGE = 'Submit';
const LABEL_MESSAGE_SENT = 'Message sent!';
const LABEL_SENDING_MESSAGE = 'Sending message...';
const LABEL_TRY_AGAIN = 'Try again';

const Form = (props) => {
    const [state, dispatch] = useReducer(reducer, initState);
    const captchaToken = getFormValue(state, formKeys.CAPTCHA_TOKEN);
    const name = getFormValue(state, formKeys.NAME);
    const email = getFormValue(state, formKeys.EMAIL);
    const company = getFormValue(state, formKeys.COMPANY);
    const note = getFormValue(state, formKeys.NOTE);
    const submissionStatus = getSubmissionStatus(state);

    useEffect(() => {
        if (submissionStatus === STATUS_SUCCESS) {
            props.onSubmissionSuccess();
        }
    }, [submissionStatus]);

    const submitForm = () => {
        const requestBody = {
            action: 'sendANote',
            eventid: bootstrap.get('eventId'),
            name,
            email,
            company,
            note,
            captchaValue: captchaToken,
        };

        dispatch(setSubmissionStatus(STATUS_LOADING));
        axios({
            method: 'POST',
            url: '',
            headers: { 'content-type': 'application/x-www-form-urlencoded' },
            data: qs.stringify(requestBody),
        })
            .then((res) => {
                if (res && res.data && res.data.error) {
                    throw res.data.error;
                }
                return res;
            })
            .then((_) => dispatch(setSubmissionStatus(STATUS_SUCCESS)))
            .catch((_) => dispatch(setSubmissionStatus(STATUS_ERROR)));
    };

    const validateForm = () => {
        const requiredValues = [formKeys.EMAIL, formKeys.NOTE, formKeys.CAPTCHA_TOKEN];

        const errors = [
            ...requiredValues.map((key) => {
                if (getFormValue(state, key)) {
                    return null;
                }

                return { key, message: 'This value is required.' };
            }),
            !isValidEmail(email) && {
                key: formKeys.EMAIL,
                message: 'Please, insert a valid email.',
            },
        ].filter(Boolean);

        errors.forEach(({ key, message }) => dispatch(setError(key, message)));

        const isFormValid = errors.length === 0;

        return isFormValid;
    };

    return (
        <SuiContainer variation="border standard flex-vertical">
            <SuiModalContent>
                <HeaderText>{HEADER_TITLE}</HeaderText>
                <InputContainer>
                    <SuiTextInput
                        label={LABEL_NAME}
                        variation={'form'}
                        placeholder={PLACEHOLDER_NAME}
                        fullWidth
                        errorText={getFormError(state, formKeys.NAME)}
                        onChange={(_, value) => dispatch(setValue(formKeys.NAME, value))}
                    />
                </InputContainer>
                <InputContainer>
                    <SuiTextInput
                        label={
                            <p>
                                {LABEL_EMAIL}
                                <Asterisk> *</Asterisk>
                            </p>
                        }
                        placeholder={PLACEHOLDER_EMAIL}
                        variation={'form'}
                        type="email"
                        fullWidth
                        errorText={getFormError(state, formKeys.EMAIL)}
                        onChange={(_, value) => dispatch(setValue(formKeys.EMAIL, value))}
                    />
                </InputContainer>
                <InputContainer>
                    <SuiTextInput
                        label={LABEL_COMPANY}
                        variation={'form'}
                        fullWidth
                        errorText={getFormError(state, formKeys.COMPANY)}
                        onChange={(_, value) => dispatch(setValue(formKeys.COMPANY, value))}
                    />
                </InputContainer>
                <InputContainer>
                    <SuiTextInput
                        label={
                            <p>
                                {LABEL_NOTE}
                                <Asterisk> *</Asterisk>
                            </p>
                        }
                        multiLine
                        rows={5}
                        fullWidth
                        variation={'form'}
                        errorText={getFormError(state, formKeys.NOTE)}
                        onChange={(_, value) => dispatch(setValue(formKeys.NOTE, value))}
                    />
                </InputContainer>
                {submissionStatus !== STATUS_SUCCESS && (
                    <RecaptchaContainer>
                        <Recaptcha
                            onVerify={(token) => {
                                dispatch(setValue(formKeys.CAPTCHA_TOKEN, token));
                            }}
                            onExpired={() => {
                                dispatch(setValue(formKeys.CAPTCHA_TOKEN, null));
                            }}
                        />
                    </RecaptchaContainer>
                )}
                <SubmitButtonContainer>
                    {
                        {
                            [STATUS_IDLE]: (
                                <SuiButton
                                    showFocusIndicator
                                    label={LABEL_SEND_MESSAGE}
                                    variation="small"
                                    onClick={() => {
                                        if (validateForm()) {
                                            submitForm();
                                        }
                                    }}
                                />
                            ),
                            [STATUS_LOADING]: <SuiButton label={LABEL_SENDING_MESSAGE} variation="small" disabled />,
                            [STATUS_SUCCESS]: <SuiButton label={LABEL_MESSAGE_SENT} variation="small" disabled />,
                            [STATUS_ERROR]: (
                                <React.Fragment>
                                    <SuiButton
                                        showFocusIndicator
                                        label={LABEL_TRY_AGAIN}
                                        variation="small"
                                        onClick={() => {
                                            if (validateForm()) {
                                                submitForm();
                                            }
                                        }}
                                    />
                                    <p>{LABEL_SUBMISSION_ERROR}</p>
                                </React.Fragment>
                            ),
                        }[submissionStatus]
                    }
                </SubmitButtonContainer>
            </SuiModalContent>
        </SuiContainer>
    );
};

Form.propTypes = {
    onSubmissionSuccess: PropTypes.func,
};

Form.defaultProps = {
    onSubmissionSuccess: noop,
};

export default Form;
